import { PageTransition } from '../PageTransition';
import type { PageTransitionVariant } from '../PageTransition';
import { STAGGER_STEP_SECONDS } from './Stagger.constants';
import { useStaggerBaseIndex } from './StaggerGroup';
import type { StaggerItemProps } from './Stagger.types';

/**
 * Maps the stagger's semantic variant onto a `PageTransition` variant.
 * `rise` reuses the original Projects-page entrance — drop down from above
 * (`slideDown`, y: -20 → 0) — which reads as the more polished cascade than
 * rising from below. `swipeUp` rises from below (`slideUp`, y: 16 → 0).
 * `fade` is opacity-only.
 */
const VARIANT_TO_PAGE_TRANSITION: Record<
  StaggerItemProps['variant'] & string,
  PageTransitionVariant
> = {
  rise: 'slideDown',
  swipeUp: 'slideUp',
  fade: 'fade',
};

/**
 * One step in a page's staggered entrance.
 *
 * Wraps the existing {@link PageTransition} so we inherit its easing, duration,
 * and `prefers-reduced-motion` fallback for free, while encoding the stagger
 * cadence in ONE place: callers pass a semantic `index` (0 = header,
 * 1 = description, 2 = filters, 3 = content) instead of a hand-typed delay, so
 * every page cascades on the identical 100ms rhythm and can never drift.
 *
 *   <StaggerItem index={0}>
 *     <SectionPageHeader>
 *       <SectionPageHeaderContent>
 *         <SectionPageHeaderTitle>Projects</SectionPageHeaderTitle>
 *         <StaggerItem index={1} variant="fade">
 *           <SectionPageHeaderDescription>…</SectionPageHeaderDescription>
 *         </StaggerItem>
 *       </SectionPageHeaderContent>
 *     </SectionPageHeader>
 *   </StaggerItem>
 *
 * The description nests inside the (rising) header card, so it uses
 * `variant="fade"` to avoid a compounded double-translate.
 */
export function StaggerItem({
  index,
  variant = 'rise',
  className,
  style,
  children,
}: StaggerItemProps) {
  // A surrounding `StaggerGroup` shifts local indices so a block cascades after
  // an earlier sequence (e.g. content after the header); 0 without one.
  const baseIndex = useStaggerBaseIndex();

  return (
    <PageTransition
      variant={VARIANT_TO_PAGE_TRANSITION[variant]}
      delay={(baseIndex + index) * STAGGER_STEP_SECONDS}
      className={className}
      style={style}
    >
      {children}
    </PageTransition>
  );
}

// Surfaced so `SectionPageHeaderContent`'s child-routing (which splits children
// by displayName) treats a wrapped description as a non-title row.
StaggerItem.displayName = 'StaggerItem';
